// Pure readout maths for the static body pages. Used in the browser by
// live.js and at build time by scripts/site/body-page.js, which bakes the
// build-day values into the HTML so the page reads correctly without JS.

import { getOrbitState, getPlanetPosition, SUPPORTED_PLANETS } from '../orbits/ephemeris.js';
import { EARTH_OBLIQUITY, equationOfTimeMinutes } from '../orbits/earthframe.js';
import { moonPosition } from '../orbits/moon.js';
import { moonGeometry, nextPhaseJd, phaseName, phasePath } from '../sky/moonphase.js';
import { jdTT } from '../astro/coords.js';
import { vsopSeries } from '../astro/vsop87series.js';
import EARTH_VSOP from '../astro/vsop87earth.js';

export { SUPPORTED_PLANETS };

const AU_KM = 149597870.7;
const C_KM_S = 299792.458;
const GM_SUN = 1.32712440018e11; // km^3 / s^2

const nf0 = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });
const nf2 = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// The numeric readouts in another language (src/i18n/config.js): digits
// grouped and the decimal mark placed as that language writes them. English
// keeps the formats above exactly. The units (km, km/s, s, min, h, AU) are
// symbols and stay as they are; the readouts made of words (season, trend,
// sky position, phase name) are English only.
const formats = new Map();
function numberFormat(locale, digits) {
  const key = `${locale}|${digits}`;
  if (!formats.has(key)) formats.set(key, new Intl.NumberFormat(locale, { minimumFractionDigits: digits, maximumFractionDigits: digits }));
  return formats.get(key);
}
const isEnglish = (locale) => !locale || /^en\b/i.test(locale);
const int = (x, locale) => (isEnglish(locale) ? nf0.format(x) : numberFormat(locale, 0).format(x));
const dec2 = (x, locale) => (isEnglish(locale) ? nf2.format(x) : numberFormat(locale, 2).format(x));
const fixed = (x, digits, locale) => (isEnglish(locale) ? x.toFixed(digits) : numberFormat(locale, digits).format(x));

// JPL's "earth" elements follow the Earth-Moon barycentre, which sits about
// 4,700 km from Earth's centre on the Moon's side. Taking the Moon's pull
// back out puts perihelion on the right day and Earth where it really is.
const MOON_MASS_SHARE = 1 / 82.30057;
function earthCentre(jd) {
  const b = getPlanetPosition('earth', jd);
  const m = moonPosition(jd);
  const k = MOON_MASS_SHARE / AU_KM;
  return { x: b.x - m.x * k, y: b.y - m.y * k, z: b.z - m.z * k };
}

function helioKm(body, jd) {
  if (body === 'earth') {
    const e = earthCentre(jd);
    return Math.hypot(e.x, e.y, e.z) * AU_KM;
  }
  return getOrbitState(body, jd).r * AU_KM;
}

function earthDistanceKm(body, jd) {
  const a = getPlanetPosition(body, jd);
  const b = earthCentre(jd);
  return Math.hypot(a.x - b.x, a.y - b.y, a.z - b.z) * AU_KM;
}

// Vis-viva: the orbital speed at the current distance.
function speedKmS(body, jd) {
  const s = getOrbitState(body, jd);
  return Math.sqrt(GM_SUN * (2 / (s.r * AU_KM) - 1 / (s.a * AU_KM)));
}

function lightTime(km, locale) {
  const seconds = km / C_KM_S;
  if (seconds < 60) return `${fixed(seconds, 2, locale)} s`;
  if (seconds < 3600) {
    const m = Math.floor(seconds / 60);
    return `${m} min ${fixed(seconds - m * 60, 1, locale)} s`;
  }
  const h = Math.floor(seconds / 3600);
  return `${h} h ${Math.round((seconds - h * 3600) / 60)} min`;
}

// The Sun's ecliptic longitude seen from Earth, in degrees [0, 360).
function sunLongitude(jd) {
  const e = getPlanetPosition('earth', jd);
  return ((Math.atan2(e.y, e.x) * 180) / Math.PI + 180 + 360) % 360;
}

const SEASONS_NORTH = ['Spring', 'Summer', 'Autumn', 'Winter'];
const SEASONS_SOUTH = ['Autumn', 'Winter', 'Spring', 'Summer'];
const EVENTS = ['March equinox', 'June solstice', 'September equinox', 'December solstice'];

function seasonText(jd) {
  const q = Math.floor(sunLongitude(jd) / 90) % 4;
  return `${SEASONS_NORTH[q]} in the north, ${SEASONS_SOUTH[q].toLowerCase()} in the south`;
}

// Steps forward in quarter days until the Sun crosses the next multiple of
// 90 degrees; a fixed mean rate is two days out near the solstices, because
// the Sun's apparent motion speeds up towards perihelion in January.
function nextSeasonText(jd) {
  const start = sunLongitude(jd);
  const next = (Math.floor(start / 90) + 1) % 4;
  let prev = start;
  let t = jd;
  for (let i = 0; i < 4 * 100; i++) {
    t += 0.25;
    const lon = sunLongitude(t);
    const crossed = next === 0 ? lon < prev : lon >= next * 90 && prev < next * 90;
    if (crossed) break;
    prev = lon;
  }
  const d = Math.round(t - jd);
  return `${EVENTS[next]} in ${d} day${d === 1 ? '' : 's'}`;
}

function orbitProgressText(body, jd) {
  const s = getOrbitState(body, jd);
  const frac = (((s.meanAnomaly / (2 * Math.PI)) % 1) + 1) % 1;
  return `${Math.round(frac * 100)}% of the way from perihelion`;
}

// ---- Distance from Earth, for the /distance/ pages ------------------------
// The planets from JPL's elements (ephemeris.js), the Moon from Meeus's lunar
// series (moon.js), the Sun as Earth's own distance from it.

export const DISTANCE_BODIES = ['sun', 'moon', ...SUPPORTED_PLANETS.filter((b) => b !== 'earth')];

const KM_PER_MILE = 1.609344;

// The Sun's distance is the Earth's centre from VSOP87A (Astronomy mode's
// series, Earth only), within 40 km of JPL Horizons in 2026-2027; the JPL
// elements above, even with the Moon's pull taken out, were up to 7,200 km off.
const _sun = [0, 0, 0];
export function sunDistanceKm(jd) {
  return Math.hypot(...vsopSeries(EARTH_VSOP, jdTT(jd), _sun)) * AU_KM;
}

export function distanceFromEarthKm(body, jd) {
  if (body === 'moon') {
    const p = moonPosition(jd);
    return Math.hypot(p.x, p.y, p.z);
  }
  if (body === 'sun') return sunDistanceKm(jd);
  return earthDistanceKm(body, jd);
}

// How fast the distance is changing, km/s: negative while it closes.
export function rangeRateKmS(body, jd) {
  const h = 1 / 1440; // a minute either side
  return (distanceFromEarthKm(body, jd + h) - distanceFromEarthKm(body, jd - h)) / (2 * h * 86400);
}

function trendText(body, jd) {
  const rate = rangeRateKmS(body, jd);
  const verb = rate < 0 ? 'getting closer' : 'moving away';
  const speed = Math.abs(rate) < 1
    ? `${nf0.format(Math.abs(rate) * 3600)} km/h`
    : `${nf2.format(Math.abs(rate))} km/s`;
  return `${verb} at ${speed}`;
}

// The same in miles per hour, for the miles page.
function trendMilesText(body, jd) {
  const rate = rangeRateKmS(body, jd);
  return `${rate < 0 ? 'getting closer' : 'moving away'} at ${nf0.format((Math.abs(rate) * 3600) / KM_PER_MILE)} mph`;
}

// Days between one close approach and the next: the Moon's anomalistic
// month, Earth's year for the Sun, and each planet's synodic period.
export function approachPeriodDays(body) {
  if (body === 'moon') return 27.5546;
  if (body === 'sun') return 365.2596;
  const year = (a) => 365.25636 * Math.pow(a, 1.5);
  const p = year(getOrbitState(body, 2451545).a);
  const e = year(getOrbitState('earth', 2451545).a);
  return 1 / Math.abs(1 / e - 1 / p);
}

// The next time the distance reaches a minimum after jd: stepped through at
// a twentieth of the approach period, then narrowed by golden section.
// Cached per body and hour, since the pages ask for the date and the
// distance separately.
const closestCache = new Map();
export function nextClosest(body, jd) {
  const cacheKey = `${body}:${Math.floor(jd * 24)}`;
  if (closestCache.has(cacheKey)) return closestCache.get(cacheKey);
  const d = (t) => distanceFromEarthKm(body, t);
  const step = approachPeriodDays(body) / 60;
  let t = jd, prev = d(jd), cur = d(jd + step);
  for (let i = 0; i < 400; i++) {
    const next = d(t + 2 * step);
    if (cur <= prev && cur <= next) break;
    t += step; prev = cur; cur = next;
  }
  let lo = t, hi = t + 2 * step;
  const g = (Math.sqrt(5) - 1) / 2;
  for (let i = 0; i < 60; i++) {
    const a = hi - g * (hi - lo), b = lo + g * (hi - lo);
    if (d(a) < d(b)) hi = b; else lo = a;
  }
  const at = (lo + hi) / 2;
  const out = { jd: at, km: d(at) };
  closestCache.set(cacheKey, out);
  return out;
}

// The closest and farthest this body can be from Earth, from the orbits'
// sizes and shapes (inclination ignored, so these are round figures).
export function distanceRangeKm(body) {
  if (body === 'moon') return { min: 356400, max: 406700 };
  const e = getOrbitState('earth', 2451545);
  if (body === 'sun') return { min: e.a * (1 - e.e) * AU_KM, max: e.a * (1 + e.e) * AU_KM };
  const p = getOrbitState(body, 2451545);
  const min = p.a > e.a ? p.a * (1 - p.e) - e.a * (1 + e.e) : e.a * (1 - e.e) - p.a * (1 + p.e);
  return { min: min * AU_KM, max: (p.a * (1 + p.e) + e.a * (1 + e.e)) * AU_KM };
}

const DATE_FMT = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' });
const TIME_FMT = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' });
const jdToDate = (jd) => new Date((jd - 2440587.5) * 86400000);

// The Moon's perigee comes round every four weeks, so it gets its hour.
export function formatApproachDate(body, jd) {
  const date = jdToDate(jd);
  return body === 'moon' ? `${DATE_FMT.format(date)}, ${TIME_FMT.format(date)} UTC` : DATE_FMT.format(date);
}

export function formatKm(km, locale) { return `${int(km, locale)} km`; }
export function formatMillionKm(km) {
  return km >= 1e9 ? `${(km / 1e9).toFixed(2)} billion km` : km >= 1e6 ? `${(km / 1e6).toFixed(1)} million km` : `${nf0.format(km)} km`;
}

function auText(km, locale) {
  const au = km / AU_KM;
  return `${fixed(au, au < 0.1 ? 6 : 4, locale)} AU`;
}

// ---- Where a planet is in the sky, for /visible-tonight/ ------------------
// The angle between the planet and the Sun as seen from Earth, and which side
// of the Sun it is on: east of the Sun it sets after sunset (evening sky),
// west of it it rises before sunrise (morning sky). Any observer, anywhere.
export function elongation(body, jd) {
  const e = getPlanetPosition('earth', jd);
  const p = getPlanetPosition(body, jd);
  const v = { x: p.x - e.x, y: p.y - e.y, z: p.z - e.z };
  const cos = -(v.x * e.x + v.y * e.y + v.z * e.z) / (Math.hypot(v.x, v.y, v.z) * Math.hypot(e.x, e.y, e.z));
  let dl = Math.atan2(v.y, v.x) - Math.atan2(-e.y, -e.x);
  dl = Math.atan2(Math.sin(dl), Math.cos(dl));
  return { deg: (Math.acos(Math.max(-1, Math.min(1, cos))) * 180) / Math.PI, evening: dl > 0 };
}

function skyText(body, jd) {
  const { deg, evening } = elongation(body, jd);
  const d = Math.round(deg);
  if (deg < 15) return `Lost in the Sun's glare, ${d}° from the Sun`;
  if (deg > 150) return `Up all night, ${d}° from the Sun`;
  return evening ? `Evening sky, ${d}° east of the Sun` : `Morning sky, ${d}° west of the Sun`;
}

export const READOUTS = {
  'distance-sun': (b, jd, locale) => `${int(helioKm(b, jd), locale)} km`,
  'distance-earth': (b, jd, locale) => `${int(earthDistanceKm(b, jd), locale)} km`,
  speed: (b, jd, locale) => `${dec2(speedKmS(b, jd), locale)} km/s`,
  'light-sun': (b, jd, locale) => lightTime(helioKm(b, jd), locale),
  'light-earth': (b, jd, locale) => lightTime(earthDistanceKm(b, jd), locale),
  'orbit-progress': orbitProgressText,
  season: (b, jd) => seasonText(jd),
  'next-season': (b, jd) => nextSeasonText(jd),
  'dist-km': (b, jd, locale) => formatKm(distanceFromEarthKm(b, jd), locale),
  'dist-mi': (b, jd) => `${nf0.format(distanceFromEarthKm(b, jd) / KM_PER_MILE)} miles`,
  'dist-mi-m': (b, jd) => `${nf2.format(distanceFromEarthKm(b, jd) / KM_PER_MILE / 1e6)} million miles`,
  'dist-trend-mi': trendMilesText,
  'dist-au': (b, jd, locale) => auText(distanceFromEarthKm(b, jd), locale),
  'dist-light': (b, jd, locale) => lightTime(distanceFromEarthKm(b, jd), locale),
  'dist-trend': trendText,
  'next-closest-date': (b, jd) => formatApproachDate(b, nextClosest(b, jd).jd),
  'next-closest-km': (b, jd, locale) => formatKm(nextClosest(b, jd).km, locale),
  'sky-now': skyText,
  'moon-phase': (b, jd) => moonPhaseNow(jd).name,
  'moon-lit': (b, jd) => `${Math.round(moonPhaseNow(jd).lit * 100)}% lit`,
  'moon-next-full': (b, jd) => { const t = nextMoonPhaseJd(jd, 180); return t == null ? '' : formatApproachDate('moon', t); },
  'moon-next-new': (b, jd) => { const t = nextMoonPhaseJd(jd, 0); return t == null ? '' : formatApproachDate('moon', t); },
};

// The distance keys also answer for the Sun and the Moon.
const ANY_BODY = new Set(['dist-km', 'dist-mi', 'dist-mi-m', 'dist-trend-mi', 'dist-au', 'dist-light', 'dist-trend', 'next-closest-date', 'next-closest-km']);
// The phase keys answer only for the Moon.
const MOON_ONLY = new Set(['moon-phase', 'moon-lit', 'moon-next-full', 'moon-next-new']);

// locale: the page's language for the numeric readouts (English if omitted).
export function readout(key, body, jd, locale) {
  const fn = READOUTS[key];
  if (!fn) return '';
  const ok = MOON_ONLY.has(key) ? body === 'moon' : ANY_BODY.has(key) ? DISTANCE_BODIES.includes(body) : SUPPORTED_PLANETS.includes(body);
  return ok ? fn(body, jd, locale) : '';
}

// ---- The Moon's phase, for /moons/moon/ ------------------------------------
// From src/sky/moonphase.js, the same sums as the Moon phase pages
// (/moon-phase/), so every page on the site gives the same phase, lit
// fraction and times: the apparent elongation on Terrestrial Time, within
// about a minute and a half of USNO (docs/moon-phase-verification.md).

// The four principal phases are instants; the name holds for the day around
// each (the Moon gains about 12 degrees a day on the Sun, so 6 degrees
// either side), with crescent and gibbous between them.
export function moonPhaseNow(jd) {
  const { elong, lit } = moonGeometry(jd);
  return { name: phaseName(elong), lit, waxing: elong < 180, elong };
}

// The first moment after jd when the elongation reaches target (0 new Moon,
// 180 full): daily steps, then bisection to well under a second.
export const nextMoonPhaseJd = (jd, target) => nextPhaseJd(jd, target);

// The lit part of a disc of radius r centred on 0,0, as an SVG path, seen
// from the Northern Hemisphere (lit on the right while waxing).
export const moonPhasePath = phasePath;

// Where the Sun is straight overhead right now, in degrees: latitude is the
// Sun's declination, longitude is where it is local apparent noon.
export function subsolarPoint(jd) {
  const lambda = (sunLongitude(jd) * Math.PI) / 180;
  const lat = (Math.asin(Math.sin(EARTH_OBLIQUITY) * Math.sin(lambda)) * 180) / Math.PI;
  const utHours = ((jd + 0.5) % 1) * 24;
  let lon = -15 * (utHours - 12) - equationOfTimeMinutes(jd) / 4;
  lon = ((lon + 540) % 360) - 180;
  return { lat, lon };
}

// Heliocentric ecliptic longitude (degrees) and distance (AU) of every body,
// for the top-down system map.
export function systemPositions(jd) {
  return SUPPORTED_PLANETS.map((name) => {
    const p = getPlanetPosition(name, jd);
    return { name, lon: Math.atan2(p.y, p.x), au: Math.hypot(p.x, p.y) };
  });
}
